import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar
} from 'recharts';
import { 
  MdTrendingUp, 
  MdTrendingDown, 
  MdPeople, 
  MdMessage, 
  MdCampaign,
  MdPhone,
  MdGroup,
  MdCloudUpload,
  MdSettings,
  MdAnalytics,
  MdAttachMoney,
  MdSchedule
} from 'react-icons/md';

// Sample data for charts
const messageTrendData = [
  { name: 'Jan', messages: 1200, customers: 800 },
  { name: 'Feb', messages: 1900, customers: 1200 },
  { name: 'Mar', messages: 3000, customers: 1800 },
  { name: 'Apr', messages: 2800, customers: 1600 },
  { name: 'May', messages: 1890, customers: 1100 },
  { name: 'Jun', messages: 2390, customers: 1400 },
  { name: 'Jul', messages: 3490, customers: 2000 },
];

const messageTypeData = [
  { name: 'Bot Messages', value: 45, color: '#8884d8' },
  { name: 'Marketing', value: 30, color: '#82ca9d' },
  { name: 'Utility', value: 25, color: '#ffc658' },
];

const customerEngagementData = [
  { name: 'Week 1', active: 400, inactive: 240 },
  { name: 'Week 2', active: 300, inactive: 139 },
  { name: 'Week 3', active: 200, inactive: 980 },
  { name: 'Week 4', active: 278, inactive: 390 },
];

const revenueData = [
  { name: 'Q1', revenue: 12000, expenses: 8000 },
  { name: 'Q2', revenue: 19000, expenses: 12000 },
  { name: 'Q3', revenue: 30000, expenses: 18000 },
  { name: 'Q4', revenue: 28000, expenses: 16000 },
];

const performanceData = [
  { name: 'Response Time', value: 85, color: '#8884d8' },
  { name: 'Delivery Rate', value: 92, color: '#82ca9d' },
  { name: 'Customer Satisfaction', value: 78, color: '#ffc658' },
];

interface DashboardStats {
  totalMessages: number;
  totalCustomers: number;
  activeCampaigns: number;
  revenue: number;
  messageGrowth: number;
  customerGrowth: number;
}

export default function ModernDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalMessages: 0,
    totalCustomers: 0,
    activeCampaigns: 0,
    revenue: 0,
    messageGrowth: 0,
    customerGrowth: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate API call to fetch dashboard data
    const fetchDashboardData = async () => {
      setLoading(true);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock data - replace with actual API calls
      setStats({
        totalMessages: 15420,
        totalCustomers: 3240,
        activeCampaigns: 8,
        revenue: 45600,
        messageGrowth: 12.5,
        customerGrowth: 8.3,
      });
      
      setLoading(false);
    };

    fetchDashboardData();
  }, []);

  const StatCard = ({ title, value, icon, growth, color = "blue" }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    growth?: number;
    color?: string;
  }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {growth !== undefined && (
            <div className="flex items-center mt-2">
              {growth >= 0 ? (
                <MdTrendingUp className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <MdTrendingDown className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {growth >= 0 ? '+' : ''}{growth}%
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <div className={`text-${color}-600`}>{icon}</div>
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = "" }: {
    title: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                WhatsApp Analytics Dashboard
              </h1>
              <p className="text-gray-600">
                Comprehensive insights into your WhatsApp Business performance
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                <MdAnalytics className="w-4 h-4 mr-2 inline" />
                Export Report
              </button>
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                <MdSchedule className="w-4 h-4 mr-2 inline" />
                Schedule Report
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Messages"
            value={stats.totalMessages.toLocaleString()}
            icon={<MdMessage className="w-6 h-6" />}
            growth={stats.messageGrowth}
            color="blue"
          />
          <StatCard
            title="Active Customers"
            value={stats.totalCustomers.toLocaleString()}
            icon={<MdPeople className="w-6 h-6" />}
            growth={stats.customerGrowth}
            color="green"
          />
          <StatCard
            title="Active Campaigns"
            value={stats.activeCampaigns}
            icon={<MdCampaign className="w-6 h-6" />}
            color="purple"
          />
          <StatCard
            title="Revenue"
            value={`$${stats.revenue.toLocaleString()}`}
            icon={<MdAttachMoney className="w-6 h-6" />}
            growth={15.2}
            color="yellow"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Message Trends */}
          <ChartCard title="Message Trends & Customer Growth" className="lg:col-span-2">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={messageTrendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="messages" 
                  stroke="#8884d8" 
                  strokeWidth={3}
                  name="Messages Sent"
                />
                <Line 
                  type="monotone" 
                  dataKey="customers" 
                  stroke="#82ca9d" 
                  strokeWidth={3}
                  name="Active Customers"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Message Types Distribution */}
          <ChartCard title="Message Types Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={messageTypeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {messageTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Customer Engagement */}
          <ChartCard title="Customer Engagement">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={customerEngagementData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Area 
                  type="monotone" 
                  dataKey="active" 
                  stackId="1" 
                  stroke="#82ca9d" 
                  fill="#82ca9d" 
                  name="Active Users"
                />
                <Area 
                  type="monotone" 
                  dataKey="inactive" 
                  stackId="1" 
                  stroke="#ffc658" 
                  fill="#ffc658" 
                  name="Inactive Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Revenue vs Expenses */}
          <ChartCard title="Revenue vs Expenses">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="revenue" fill="#8884d8" name="Revenue" />
                <Bar dataKey="expenses" fill="#ffc658" name="Expenses" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Performance Metrics */}
          <ChartCard title="Performance Metrics">
            <ResponsiveContainer width="100%" height={300}>
              <RadialBarChart cx="50%" cy="50%" innerRadius="20%" outerRadius="80%" data={performanceData}>
                <RadialBar dataKey="value" />
                <Tooltip />
                <Legend />
              </RadialBarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <button 
              onClick={() => navigate('/events/conversations')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <MdMessage className="w-8 h-8 text-blue-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">Send Message</span>
            </button>
            <button 
              onClick={() => navigate('/events/whatsapp-marketing-list')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <MdCampaign className="w-8 h-8 text-green-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">Create Campaign</span>
            </button>
            <button 
              onClick={() => navigate('/events/whatsapp-phone-groups-manager')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <MdGroup className="w-8 h-8 text-purple-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">Manage Groups</span>
            </button>
            <button 
              onClick={() => navigate('/events/file-upload')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <MdCloudUpload className="w-8 h-8 text-orange-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">Upload Media</span>
            </button>
            <button 
              onClick={() => navigate('/events/conversations')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <MdPhone className="w-8 h-8 text-red-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">Phone Numbers</span>
            </button>
            <button 
              onClick={() => navigate('/events/user-settings')}
              className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <MdSettings className="w-8 h-8 text-gray-500 mb-2" />
              <span className="text-sm font-medium text-gray-700">Settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}